/**
 * ENG-9678: host-side memory content cache, and the resolver that turns
 * `/host/memories`' `memories_manifest` back into the full entries
 * `downloadMemories` expects.
 *
 * WHAT THIS BUYS. The poll used to carry every live memory body to its agent
 * every ~10s so the manager could render one `.md` per memory and then
 * discover, by byte comparison, that the file on disk was already identical.
 * Measured on prod (24h to 2026-08-31T02:20Z): 207,526 requests, 11.7 GB,
 * 57.9 KB average — 28.6% of all fleet egress. Now the poll carries
 * `{ name, type, hash }` and bodies are fetched once and kept.
 *
 * CONTENT-ADDRESSED, AND PER-AGENT. This is the deliberate divergence from
 * `knowledge-cache.ts`, which is host-wide, and copying that choice here would
 * be wrong. Knowledge is SHARED by construction — platform-global and org
 * entries are byte-identical for every agent on the host, so one store lets the
 * first agent's fetch serve the rest, and that cross-agent dedupe is most of
 * what makes it worth having. Memories are AGENT-PRIVATE: two agents sharing a
 * memory body is a coincidence, not the normal case, so a shared store buys
 * approximately nothing while placing one agent's private content at a path
 * every other agent on the host can read. All the benefit here is TEMPORAL —
 * the same agent, across polls — and a per-agent store keeps all of it.
 *
 * FAILS SOFT, like knowledge and NOT like skills. `resolveSkillsFromManifest`
 * is all-or-nothing because `planGlobalSkillSync` PRUNES against the desired
 * set, so a partial list would delete the agent's skill folders. Memories have
 * the opposite contract: absence from the list has never implied deletion (the
 * ENG-9257 tombstone rule — only explicit presence in `tombstones` removes a
 * file), so an unresolved body means one `.md` is not rewritten this tick and
 * the copy already on disk is untouched. That self-heals on the next poll.
 *
 * WHY THE RESOLVER RETURNS ONLY RESOLVED ENTRIES. `downloadMemories` builds
 * each file's frontmatter with `mem.content.slice(0, 200)`. Handing it an entry
 * whose body did not resolve would throw inside the poll, be swallowed by the
 * surrounding try/catch, and turn one missing body into "memory download
 * failed" for the whole agent. Filtering here makes that unrepresentable rather
 * than relying on every future caller to remember the null check.
 */

import { createHash } from 'node:crypto';
import {
  existsSync,
  mkdirSync,
  readFileSync,
  readdirSync,
  renameSync,
  rmSync,
  statSync,
  utimesSync,
  writeFileSync,
} from 'node:fs';
import { join } from 'node:path';

/**
 * One entry as `/host/memories` lists it in manifest mode — a WIRE manifest.
 *
 * Deliberately NOT called `MemoryManifestEntry`: manager-worker.ts already has
 * a type by that name for the ON-DISK manifest of files this host wrote
 * (`{ file, sha }` keyed by memory name), which the ENG-9257 tombstone path
 * uses to prove a file is attributable before deleting it. Two different things
 * under one name, in the same file, where one of them gates a delete, is not a
 * collision worth resolving with an import alias.
 */
export interface MemoryContentManifestEntry {
  name: string;
  type: string;
  hash?: string;
}

/** A fully-resolved memory, the shape `downloadMemories` has always consumed. */
export interface ResolvedMemory {
  name: string;
  type: string;
  content: string;
}

/**
 * Hashes per fetch request. Must not exceed the API's `MAX_MEMORY_FETCH`
 * (`packages/api/src/routes/host-runtime.ts`), which rejects a larger request
 * with a 400.
 */
export const MEMORY_FETCH_CHUNK = 100;

/** Untouched-for-this-long cached bodies are swept. A wrong sweep costs one refetch. */
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** At most one sweep per cache dir per day — this runs inside a ~10s poll loop. */
const SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * Last sweep time PER cacheDir, not per process.
 *
 * One manager serves every agent on the host and passes a per-agent cacheDir
 * (`manager-worker.ts`: `join(configDir, agent.code_name, '_memory_bodies')`),
 * which is the same isolation the header above describes. A single scalar here
 * defeated the sweep half of it: the first agent to sync claimed the 24h window
 * and every OTHER agent's cache went unswept until the next day — a different
 * arbitrary winner each time, so on a pool host all but one cache grew without
 * bound, with no error and nothing in the log.
 *
 * Entries are keyed by directory and never pruned. A retired agent leaves one
 * string and one number behind, which is cheaper than any scheme for noticing
 * it is gone.
 */
const lastSweepAt = new Map<string, number>();

/**
 * sha256 of a body, matching what Postgres generates for the manifest
 * (migration 20260831000050, via `public.knowledge_content_hash`).
 *
 * UTF-8 explicitly, not the platform default: Postgres digests
 * `convert_to(content, 'UTF8')`, and memories are prose full of em dashes and
 * smart quotes that are one code unit but three bytes. A mismatched encoding
 * would verify clean on ASCII entries and reject exactly the text-heavy ones.
 *
 * sha256 rather than a truncation of it: this value is the cache FILENAME, so
 * shortening it trades collision resistance for a few bytes on a payload that
 * is already tiny, and a collision here serves one memory's body under another
 * memory's name.
 */
export function memoryHash(content: string): string {
  return createHash('sha256').update(content, 'utf8').digest('hex');
}

function cachePath(cacheDir: string, hash: string): string {
  return join(cacheDir, hash);
}

/**
 * Read a cached body, or null.
 *
 * Touches the file's mtime on a hit so the sweep below measures LAST USE rather
 * than last write — otherwise a body read on every poll but never rewritten
 * would age out and be refetched for no reason.
 */
export function readCachedMemory(cacheDir: string, hash: string): string | null {
  const path = cachePath(cacheDir, hash);
  try {
    const content = readFileSync(path, 'utf-8');
    try {
      const now = new Date();
      utimesSync(path, now, now);
    } catch { /* touch is an optimisation, never a failure */ }
    return content;
  } catch {
    return null;
  }
}

/**
 * Store a body under its hash, but only if it actually hashes to that.
 *
 * The verification is the point: it is what makes the cache safe to trust on
 * every subsequent poll without re-checking. A truncated or mis-mapped response
 * is rejected here — where it costs one refetch — instead of being written once
 * and then served as authoritative content for as long as the file survives.
 * Returns whether the body was accepted.
 */
export function writeCachedMemory(cacheDir: string, hash: string, content: string): boolean {
  if (memoryHash(content) !== hash) return false;
  try {
    mkdirSync(cacheDir, { recursive: true });
    // Write-then-rename: a manager killed mid-write must not leave a truncated
    // body at a name that claims to be a verified hash.
    const tmp = `${cachePath(cacheDir, hash)}.tmp-${process.pid}`;
    writeFileSync(tmp, content, 'utf-8');
    renameSync(tmp, cachePath(cacheDir, hash));
    return true;
  } catch {
    return false;
  }
}

/** Drop bodies untouched for CACHE_TTL_MS. Best-effort; rate-limited per cache dir. */
export function sweepMemoryCache(cacheDir: string, now = Date.now()): void {
  if (now - (lastSweepAt.get(cacheDir) ?? 0) < SWEEP_INTERVAL_MS) return;
  lastSweepAt.set(cacheDir, now);
  try {
    if (!existsSync(cacheDir)) return;
    for (const name of readdirSync(cacheDir)) {
      const path = join(cacheDir, name);
      try {
        if (now - statSync(path).mtimeMs > CACHE_TTL_MS) rmSync(path, { force: true });
      } catch { /* a body we cannot stat or remove is not worth failing a poll over */ }
    }
  } catch { /* non-fatal */ }
}

/** Resets the sweep rate-limiter. Test seam only. */
export function __resetSweepClock(): void {
  lastSweepAt.clear();
}

export interface ResolveMemoriesArgs {
  manifest: MemoryContentManifestEntry[];
  cacheDir: string;
  /** Fetches bodies for the given hashes. Chunked by this module. */
  fetchContents: (hashes: string[]) => Promise<Array<{ hash: string; content: string }>>;
  log?: (msg: string) => void;
}

export interface ResolveMemoriesResult {
  /** Only entries whose body resolved. Order matches the manifest. */
  memories: ResolvedMemory[];
  /** How many manifest entries could not be resolved this tick. */
  unresolved: number;
}

/**
 * Turn a manifest into the entries `downloadMemories` writes, fetching only the
 * bodies this host does not already have.
 *
 * Fails SOFT, deliberately — see the module header. An entry whose body cannot
 * be resolved is omitted from `memories` and counted in `unresolved`; the file
 * already on disk is untouched, because absence never implies deletion.
 */
export async function resolveMemoriesFromManifest(
  args: ResolveMemoriesArgs,
): Promise<ResolveMemoriesResult> {
  const { manifest, cacheDir, fetchContents, log } = args;

  const bodies = new Map<string, string>();
  // ORDERED array + Set, deliberately both. The array is what gets chunked, so
  // fetch order stays manifest order (which the resolver's ordering contract
  // depends on); the Set is only for membership, because `missing.includes` is
  // O(n) per entry and made a cold cache O(n^2) on the ~10s poll path.
  const missing: string[] = [];
  const missingSeen = new Set<string>();
  for (const entry of manifest) {
    if (!entry.hash || bodies.has(entry.hash)) continue;
    const cached = readCachedMemory(cacheDir, entry.hash);
    if (cached !== null) bodies.set(entry.hash, cached);
    else if (!missingSeen.has(entry.hash)) {
      missingSeen.add(entry.hash);
      missing.push(entry.hash);
    }
  }

  if (missing.length > 0) {
    for (let i = 0; i < missing.length; i += MEMORY_FETCH_CHUNK) {
      const chunk = missing.slice(i, i + MEMORY_FETCH_CHUNK);
      try {
        for (const { hash, content } of await fetchContents(chunk)) {
          // An unverified body is DROPPED, not served (CodeRabbit, PR #5147, on
          // the knowledge equivalent). The two ways to get here are transport
          // corruption and a server-side hash->body mapping bug, and the second
          // hands back ANOTHER MEMORY'S content. Writing that into the agent's
          // memory directory is worse than writing nothing: a missing file is
          // visibly missing and self-heals next poll, whereas a memory holding
          // someone else's content is neither.
          if (!writeCachedMemory(cacheDir, hash, content)) {
            log?.(`[memory-cache] hash mismatch for ${hash} — dropping this poll`);
            continue;
          }
          bodies.set(hash, content);
        }
      } catch (err) {
        log?.(`[memory-cache] fetch failed for ${chunk.length} entries: ${(err as Error).message}`);
      }
    }
  }

  sweepMemoryCache(cacheDir);

  const memories: ResolvedMemory[] = [];
  let unresolved = 0;
  for (const entry of manifest) {
    const content = entry.hash ? bodies.get(entry.hash) : undefined;
    if (content === undefined) {
      unresolved++;
      continue;
    }
    memories.push({ name: entry.name, type: entry.type, content });
  }

  return { memories, unresolved };
}
